// Amplitude Integration

type AmplitudeInitOptions = {
    sampleRate?: number;
    autocapture?: { elementInteractions?: boolean };
};

const initializeAmplitude = (userId?: string, options?: AmplitudeInitOptions) => {
    // guard against double initialization
    const w = window as unknown as Record<string, any>;
    // simple on-ready callback queue for events fired before SDK loads
    w.__amplitudeOnReady = w.__amplitudeOnReady || [];
    if (w.__amplitudeInitialized) {
        // If already initialized, still set userId if provided
        try {
            if (userId && w.amplitude && typeof w.amplitude.setUserId === 'function') {
                w.amplitude.setUserId(userId);
            }
            // flush any pending callbacks
            if (Array.isArray(w.__amplitudeOnReady)) {
                const callbacks: Array<() => void> = w.__amplitudeOnReady.splice(0);
                callbacks.forEach((cb) => {
                    try {
                        cb();
                    } catch {
                        // no-op
                    }
                });
            }
        } catch {
            // no-op
        }
        return;
    }

    const apiKey = process.env.REACT_APP_AMPLITUDE_API_KEY as string;
    if (!apiKey) {
        // missing API key; skip initialization
        try {
            console.warn('[Amplitude] REACT_APP_AMPLITUDE_API_KEY is missing. Skipping init.');
        } catch {
            // no-op
        }
        return;
    }
    const analyticsSrc = 'https://cdn.amplitude.com/libs/analytics-browser-2.11.1-min.js.gz';
    const sessionReplaySrc = 'https://cdn.amplitude.com/libs/plugin-session-replay-browser-1.8.0-min.js.gz';
    const engagementSrc = `https://cdn.amplitude.com/script/${apiKey}.engagement.js`;

    const insertBeforeFirstScript = (script: HTMLScriptElement) => {
        const firstScript = document.getElementsByTagName('script')[0];
        firstScript?.parentNode?.insertBefore(script, firstScript);
    };

    // load core analytics browser
    const analyticsScript = document.createElement('script');
    analyticsScript.async = true;
    analyticsScript.src = analyticsSrc;

    analyticsScript.onload = () => {
        // load session replay plugin after core is available
        const sessionScript = document.createElement('script');
        sessionScript.async = true;
        sessionScript.src = sessionReplaySrc;
        sessionScript.onerror = () => {
            try {
                console.error('[Amplitude] Failed to load Session Replay plugin:', sessionReplaySrc);
            } catch {
                // no-op
            }
        };

        sessionScript.onload = () => {
            try {
                const amplitude = (w as any).amplitude;
                const sessionReplay = (w as any).sessionReplay;

                if (amplitude && sessionReplay && typeof amplitude.add === 'function') {
                    const sampleRate = options?.sampleRate ?? 1;
                    amplitude.add(sessionReplay.plugin({ sampleRate }));
                }

                // initialize amplitude with apiKey, userId and options
                if (amplitude && typeof amplitude.init === 'function') {
                    const initOptions = {
                        autocapture: { elementInteractions: true, ...(options?.autocapture || {}) },
                        ...options
                    } as Record<string, unknown>;
                    // remove our non-amplitude helper fields before passing through
                    delete (initOptions as any).apiKey;
                    delete (initOptions as any).sampleRate;

                    amplitude.init(apiKey, userId, initOptions);
                    try {
                        console.debug('[Amplitude] Initialized with key and userId:', !!apiKey, !!userId);
                    } catch {
                        // no-op
                    }
                    // mark initialized and flush pending callbacks immediately after init
                    w.__amplitudeInitialized = true;
                    if (Array.isArray(w.__amplitudeOnReady)) {
                        const callbacks: Array<() => void> = w.__amplitudeOnReady.splice(0);
                        callbacks.forEach((cb) => {
                            try {
                                cb();
                            } catch {
                                // no-op
                            }
                        });
                    }
                }

                // load engagement plugin and add it
                const engagementScript = document.createElement('script');
                engagementScript.async = true;
                engagementScript.src = engagementSrc;
                engagementScript.onload = () => {
                    try {
                        const engagement = (w as any).engagement;
                        if (engagement && (w as any).amplitude && typeof (w as any).amplitude.add === 'function') {
                            (w as any).amplitude.add(engagement.plugin());
                        }
                        // engagement plugin loaded; already marked initialized after init
                    } catch {
                        // no-op
                    }
                };
                engagementScript.onerror = () => {
                    try {
                        console.error('[Amplitude] Failed to load Engagement plugin:', engagementSrc);
                    } catch {
                        // no-op
                    }
                };
                insertBeforeFirstScript(engagementScript);
            } catch {
                // no-op
            }
        };

        insertBeforeFirstScript(sessionScript);
    };
    analyticsScript.onerror = () => {
        try {
            console.error('[Amplitude] Failed to load Analytics SDK:', analyticsSrc);
        } catch {
            // no-op
        }
    };

    insertBeforeFirstScript(analyticsScript);
};

export default initializeAmplitude;

export const identifyAmplitudeUser = (userId?: string) => {
    const w = window as unknown as Record<string, any>;
    const amplitude = (w as any).amplitude;
    if (!amplitude) return;

    try {
        if (userId && typeof amplitude.setUserId === 'function') {
            amplitude.setUserId(userId);
        }
    } catch {
        // no-op
    }
};

export const trackAmplitudeEvent = (eventName: string, eventProperties?: Record<string, unknown>) => {
    const w = window as unknown as Record<string, any>;
    const call = () => {
        const amplitude = (w as any).amplitude;
        if (!amplitude || typeof amplitude.track !== 'function') return;
        try {
            amplitude.track(eventName, eventProperties || {});
        } catch {
            // no-op
        }
    };
    if (w.__amplitudeInitialized) {
        call();
    } else {
        w.__amplitudeOnReady = w.__amplitudeOnReady || [];
        w.__amplitudeOnReady.push(call);
    }
};
